'use client';
import { isMenuItem, isTextUrl, type LegacyHeader } from '@bw/apis/contentful';
import { nonNull } from '@bw/core';
import { Image } from '@bw/elements';
import { getCookie } from 'cookies-next';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../form-stepper/StepperProvider';
import { HeaderCtaSection } from './header-cta-section';
import { DesktopMenu } from './header-menu/HeaderDesktopMenu';
import { MobileMenu } from './header-menu/HeaderMobileMenu';
import styles from './header.module.scss';

export function Header({ entry, isLockup }: { entry: LegacyHeader; isLockup?: boolean }) {
	return <ClientHeader fields={entry.fields} isLockup={isLockup} />;
}

export function ClientHeader({ fields, isLockup }: { fields: LegacyHeader['fields']; isLockup?: boolean }) {
	const [menuOpen, setMenuOpen] = useState(false);
	const headerWrapperRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const [menuHeight, setMenuHeight] = useState<number>(0); // TODO(khaney): use responsive layout
	const [activeMenuItem, setActiveMenuItem] = useState<unknown | null>(null);
	const [isMobile, setIsMobile] = useState(true); // TODO: use media query instead
	const [heightChange, setHeightChange] = useState(0);
	const { showFormStepper } = useAppContext();
	const { announcementBannerHeight } = useAppContext();
	const [activeBack, setActiveBack] = useState(false); // do we need this?
	const [activeSubmenu, setActiveSubmenu] = useState<
		unknown[] | null | undefined
	>(null);

	useEffect(() => {
		setActiveBack(false);
		// Check if window is defined to avoid server-side rendering errors
		if (typeof window !== 'undefined') {
			setIsMobile(window.innerWidth < 1150);

			const handleResize = () => {
				setIsMobile(window.innerWidth < 1150);
			};
			const handleHeightChange = () => {
				setHeightChange(window.innerHeight);
			};

			window.addEventListener('resize', handleResize);
			window.addEventListener('resize', handleHeightChange);

			return () => {
				window.removeEventListener('resize', handleResize);
				window.removeEventListener('resize', handleHeightChange);
			};
		}
	}, []);

	useEffect(() => {
		const announcementClosedCookie = getCookie('announcementClosed');
		if (!announcementClosedCookie && headerWrapperRef.current && !isMobile) {
			const headerWrapperHeight = headerWrapperRef.current.clientHeight;

			setMenuHeight(
				window.innerHeight -
					((headerWrapperHeight || 80) + announcementBannerHeight)
			);
		} else if (headerWrapperRef.current) {
			const headerWrapperHeight = headerWrapperRef.current.clientHeight;
			setMenuHeight(window.innerHeight - (headerWrapperHeight || 80));
		}
	}, [menuOpen, heightChange, isMobile, announcementBannerHeight]);

	const handleFocusWithinModal = (e) => {
		const modal = headerRef.current;
		if (!modal) {
			return;
		}
		const focusableElements = modal.querySelectorAll(
			'a, button, [tabindex]:not([tabindex="-1"])'
		);
		const focusableElementsArray =
			Array.prototype.slice.call(focusableElements);
		const firstFocusableElement = focusableElementsArray[0];
		const lastFocusableElement =
			focusableElementsArray[focusableElementsArray.length - 1];
		if (e.key === 'Tab' || e.keyCode === 9) {
			if (e.shiftKey) {
				if (document.activeElement === firstFocusableElement) {
					e.preventDefault();
					lastFocusableElement.focus();
				}
			} else {
				if (document.activeElement === lastFocusableElement) {
					e.preventDefault();
					firstFocusableElement.focus();
				}
			}
		}
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && !showFormStepper) {
				setMenuOpen(false);
				setActiveMenuItem(null);
				document.body.classList.remove('disableScroll');
				document.body.setAttribute('data-lenis-prevent', 'false');
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [menuOpen, showFormStepper]);

	useEffect(() => {
		const currentHeader = headerRef.current;
		const handleKeyDown = (event) => {
			handleFocusWithinModal(event);
		};

		if (menuOpen && currentHeader) {
			currentHeader?.addEventListener('keydown', handleKeyDown);

			return () => {
				currentHeader?.removeEventListener('keydown', handleKeyDown);
			};
		}
	}, [menuOpen]);

	const toggleMenu = () => {
		setActiveBack(false);
		setMenuOpen(!menuOpen);
		setActiveMenuItem(null);
		if (menuOpen) {
			document.body.classList.remove('disableScroll');
			document.body.setAttribute('data-lenis-prevent', 'false');
		} else {
			document.body.classList.add('disableScroll');
			document.body.setAttribute('data-lenis-prevent', 'true');
		}
	};

	const handleMenuItemClick = (menuItem: any) => {
		if (!menuOpen) {
			setMenuOpen(true);
			setActiveMenuItem(menuItem);
			setActiveSubmenu(menuItem?.fields.subMenu);
			document.body.classList.add('disableScroll');
			document.body.setAttribute('data-lenis-prevent', 'true');
		} else if (menuOpen && activeMenuItem === menuItem) {
			setMenuOpen(false);
			setActiveMenuItem(null);
			document.body.classList.remove('disableScroll');
			document.body.setAttribute('data-lenis-prevent', 'false');
		} else {
			setActiveMenuItem(menuItem);
		}
	};
	const handleMenuClose = () => {
		setActiveBack(false);
		if (menuOpen) {
			setMenuOpen(false);
			setActiveMenuItem(null);
			document.body.classList.remove('disableScroll');
			document.body.setAttribute('data-lenis-prevent', 'false');
		}

		document.body.classList.remove('disableScroll');
		document.body.setAttribute('data-lenis-prevent', 'false');
	};
	const clearActiveSubmenuOnTransition = (): void => {
		if (!menuOpen && !isMobile) {
			setActiveSubmenu(null);
		}
	};

	const menu = fields.menuItems;
	const img = fields.logo?.fields.image?.fields.file?.url || '';
	const naturalImage = fields.logo?.fields.image?.fields.file?.details?.image;
	const logoRenderWidth = isLockup ? 320 : 170;
	const logoRenderHeight =
		naturalImage?.width && naturalImage?.height
			? Math.round((logoRenderWidth * naturalImage.height) / naturalImage.width)
			: 24;
	const ctaButtons = fields.ctaLinks;
	const actionLinks = fields.actionLinks;
	const barClassName = `${styles['bar']} ${menuOpen ? styles['bar--clicked'] : ''}`;

	const onBackClick = () => {
		if (menuOpen) {
			setActiveBack(true);
		}
		setActiveMenuItem(null);
		setTimeout(() => setActiveBack(false), 500);
	};

	const onSubmenuClick = (menuItem: any) => {
		setActiveBack(false);
		setActiveMenuItem(menuItem);
	};

	return (
		<div className={styles.headerContainer}>
			<header className={styles.header} ref={headerRef}>
				<div className="container container--max">
					<div className={styles.headerWrapper} ref={headerWrapperRef}>
						<Link
							href={fields.logo?.fields.imageUrl ?? '#'}
							title={fields.logo?.fields.title ?? ''}
							className={`${styles.headerLogo}${isLockup ? ` ${styles['headerLogo--lockup']}` : ''}`}
							onClick={handleMenuClose}
						>
							<Image
								src={img}
								alt={fields.logo?.fields.altText ?? ''}
								width={logoRenderWidth}
								height={logoRenderHeight}
								preload={true}
							/>
						</Link>
						<nav aria-label="Header menu" className={styles.logoMenuWrapper}>
							<ul className={styles.navList}>
								{nonNull(menu).map((menuItem, index) => (
									<li
										key={index}
										className={`${styles.navItem} ${activeMenuItem === menuItem ? styles['navItem--activeMenu'] : ''}`}
									>
										{isMenuItem(menuItem) ? (
											<div
												tabIndex={0}
												className={styles.navContent}
												onClick={() => {
													handleMenuItemClick(menuItem);
												}}
												onKeyDown={(e) => {
													// Handle Enter key press
													if (e.key === 'Enter') {
														handleMenuItemClick(menuItem);
													}
												}}
												aria-haspopup="true"
												role="button"
											>
												<span>{menuItem.fields.title}</span>
												<ChevronDown />
											</div>
										) : (
											isTextUrl(menuItem) && (
												<span aria-haspopup="false">
													<Link
														href={menuItem.fields.url ?? '#'}
														onClick={handleMenuClose}
													>
														{menuItem.fields.text}
													</Link>
												</span>
											)
										)}
									</li>
								))}
							</ul>
						</nav>

						<button
							title="toggle menu"
							className={styles.hamburgerButton}
							type="button"
							onClick={toggleMenu}
						>
							{menuOpen ? (
								<span className={styles.hamburgerText}> Close</span>
							) : (
								<></>
							)}
							<span className={styles.hamburgerIcon}>
								<span className={barClassName}></span>
								<span className={barClassName}></span>
								<span className={barClassName}></span>
							</span>
						</button>

						<HeaderCtaSection
							ctaButtons={ctaButtons}
							className={styles.desktopCta}
							containsStartAQuoteButton={fields.containsStartAQuoteButton}
							containsBookACallButton={fields.containsBookACallButton}
						/>
					</div>

					<div
						data-testid="mega-menu"
						className={`${styles.megaMenu} ${menuOpen ? styles['megaMenu--opened'] : ''}`}
						onTransitionEnd={clearActiveSubmenuOnTransition}
					>
						{isMobile ? (
							<MobileMenu
								menuContent={menu}
								menuHeight={menuHeight}
								activeMenuItem={activeMenuItem}
								onItemSelected={onSubmenuClick}
								onBackClick={onBackClick}
								activeBack={activeBack}
								onCloseClick={handleMenuClose}
								actionLinks={actionLinks}
								ctaSection={
									<HeaderCtaSection
										ctaButtons={ctaButtons}
										heightVariant="narrow"
										className={styles.mobileButtonWrapper}
										containsStartAQuoteButton={fields.containsStartAQuoteButton}
										containsBookACallButton={fields.containsBookACallButton}
									/>
								}
							/>
						) : (
							activeSubmenu && (
								<DesktopMenu
									onMenuItemClick={handleMenuClose}
									menuHeight={menuHeight}
									activeMenuItem={activeMenuItem}
									onClose={handleMenuClose}
									actionLinks={actionLinks}
									menu={[]}
								/>
							)
						)}
					</div>
				</div>
			</header>
		</div>
	);
}

const ChevronDown = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="17"
		height="16"
		viewBox="0 0 17 16"
		fill="none"
	>
		<path
			height="20"
			d="M4.75977 6L8.75977 10L12.7598 6"
			stroke="white"
			strokeWidth="1.33333"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);
